import _ from "lodash";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useI18n } from "Hooks";
import { useRefWatcher } from "./hooks";
import classes from "./ScrollableCarousel.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import ConditionalWrapper from "Utils/ConditionalWrapper";

// IMPORTANT: This component expects only one children and that children should have overflow scroll/auto on it.
const ScrollableCarousel = ({
  children,
  scrollAmount,
  getScrollableContainerRef,
  leftButtonStyle,
  rightButtonStyle,
  chevronIconSize,
  // IMPORTANT: This only handles the case where the first item is shown and the second is shown just 10% in terms of clientWidth
  showControlDots,
  testId,
  carouselWrapperStyle,
  activeControlDotValue,
  setAttachmentCountVisible,
  isAttachmentCountVisible,
  showActiveControlDot = false,
  wrapperStyle,
}) => {
  const { t } = useI18n(["a11y"]);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [activeControlDot, setActiveControlDot] = useState(1);
  const [dotCarouselWidth, setDotCarouselWidth] = useState(0);

  const scrollableContainerRef = useRef(null);
  const dotCarouselRef = useRef(null);

  const showDesignV2 = !_.isEmpty(carouselWrapperStyle);
  const totalDots = scrollableContainerRef.current?.children?.length;
  const maxVisibleDots = 5;

  useRefWatcher(dotCarouselRef, (prevValue, currentValue) => {
    if (!_.isNil(currentValue)) {
      setDotCarouselWidth(currentValue?.offsetWidth ?? containerFallBackWidth);
    }
  });

  // scrollWidth is only shown correctly inside the effect for the initial render

  useEffect(() => {
    if (_.isNil(scrollableContainerRef.current)) return;

    // reseting to default
    setShowLeftButton(false);
    setShowRightButton(false);
    getScrollableContainerRef?.(scrollableContainerRef);

    const scrollWidth = scrollableContainerRef.current.scrollWidth;
    const clientWidth = scrollableContainerRef.current.clientWidth;
    const scrollLeft = scrollableContainerRef.current.scrollLeft;
    const maxScrollAvailable = scrollWidth - clientWidth;

    if (scrollLeft !== 0) setShowLeftButton(true);
    if (maxScrollAvailable > 0) setShowRightButton(true);
    // recalculating if scrollWidth changes
  }, [scrollableContainerRef, scrollableContainerRef.current?.scrollWidth]);

  useEffect(() => {
    showActiveControlDot &&
      activeControlDotValue &&
      setTimeout(() => {
        scrollableContainerRef?.current?.scrollTo({
          left: activeControlDotValue * scrollAmount + scrollAmount,
          behavior: "smooth",
        });
      });
  }, [activeControlDotValue]);

  const containerFallBackWidth = useMemo(() => {
    // approx width of each dot in inner container - 9.7
    return totalDots * 9.7;
  }, [totalDots]);

  const scrollRightButton = classNames({
    [classes.scrollButton]: true,
    [classes.scrollRightButton]: true,
  });
  const scrollLeftButton = classNames({
    [classes.scrollButton]: true,
    [classes.scrollLeftButton]: true,
  });

  const handleScroll = () => {
    const scrollWidth = scrollableContainerRef.current.scrollWidth;
    const clientWidth = scrollableContainerRef.current.clientWidth;
    const maxScrollAvailable = scrollWidth - clientWidth;
    const scrollLeft = scrollableContainerRef.current.scrollLeft;
    if (scrollLeft <= 0) setShowLeftButton(false);
    else setShowLeftButton(true);
    if (scrollLeft >= maxScrollAvailable) setShowRightButton(false);
    else setShowRightButton(true);

    if (showControlDots) handleActiveControlDot(scrollLeft);
  };

  const handleLeftClick = e => {
    e.stopPropagation();
    const scrollLeft = scrollableContainerRef.current.scrollLeft;
    scrollableContainerRef.current.scrollTo({
      left: scrollLeft - scrollAmount,
      behavior: "smooth",
    });

    _.isFunction(setAttachmentCountVisible) &&
      setTimeout(() => {
        handleShowAttachmentCount();
      });
  };

  const handleShowAttachmentCount = () => {
    if (
      _.isFunction(setAttachmentCountVisible) &&
      !_.isUndefined(isAttachmentCountVisible)
    ) {
      setAttachmentCountVisible(true);
      setTimeout(() => {
        setAttachmentCountVisible(false);
      }, 1000);
    }
  };

  const handleRightClick = e => {
    e.stopPropagation();
    const scrollLeft = scrollableContainerRef.current.scrollLeft;
    scrollableContainerRef.current.scrollTo({
      left: scrollLeft + scrollAmount,
      behavior: "smooth",
    });
    _.isFunction(setAttachmentCountVisible) &&
      setTimeout(() => {
        handleShowAttachmentCount();
      });
  };

  const handleActiveControlDot = scrollLeft => {
    if (scrollAmount <= 0) return;
    const currentCardInView = Math.round(
      (scrollLeft + scrollAmount) / scrollAmount
    );
    setActiveControlDot(currentCardInView);
  };

  const handleControlDotClick = scrollLeft => {
    scrollableContainerRef.current.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  };

  const getDotContainerWidth = () => {
    if (totalDots >= maxVisibleDots) {
      // width required by container to display 5 or more dots
      return 54;
    } else {
      //  dotWidth = 6, dotMargin = 4, padding=8
      return totalDots * 6 + (totalDots - 1) * 4 + 8;
    }
  };

  const getTranslateX = () => {
    if (totalDots <= maxVisibleDots) {
      return 0;
    }
    // buffer required to align dots
    let dotBuffer = 33;
    if (activeControlDot < maxVisibleDots) {
      dotBuffer = 23;
    }
    const initialTranslateX = dotCarouselWidth / 2 - dotBuffer;
    if (activeControlDot < maxVisibleDots) {
      return initialTranslateX;
    }

    // total space occupied by 1 dot = 10
    // size of each dot 6
    const translationX = initialTranslateX - 10 * (activeControlDot - 6);
    return translationX;
  };

  const getCarouselDots = index => {
    const isSmallDot =
      (activeControlDot >= maxVisibleDots &&
        index + 1 === activeControlDot &&
        activeControlDot !== totalDots) ||
      (activeControlDot >= maxVisibleDots - 1 &&
        activeControlDot !== totalDots - 1 &&
        index === activeControlDot - maxVisibleDots &&
        totalDots > maxVisibleDots) ||
      (activeControlDot === totalDots - 1 &&
        index === activeControlDot - maxVisibleDots);

    const isDotActive =
      (activeControlDot >= maxVisibleDots &&
        activeControlDot - 2 === index &&
        activeControlDot !== totalDots) ||
      (activeControlDot < maxVisibleDots && activeControlDot === index + 1) ||
      (activeControlDot === totalDots && activeControlDot - 1 === index);
    return { isSmallDot, isDotActive };
  };

  return (
    <div className={classes.scrollWrapper} style={wrapperStyle}>
      {showLeftButton ? (
        <button
          onClick={handleLeftClick}
          className={scrollLeftButton}
          style={leftButtonStyle}
          data-test-id={`${testId}-left-button`}
          aria-label={t("a11y:carousel_previous_slide")}
        >
          <ChevronLeftOutlined variant={"subtle"} size={chevronIconSize} />
        </button>
      ) : null}
      <ConditionalWrapper
        condition={showControlDots}
        wrapper={<div className={classes.wrapper} />}
        type={"wrapperAsElement"}
      >
        <>
          {React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) return null;

            if (index === 0) {
              return React.cloneElement(child, {
                onScroll: handleScroll,
                ref: scrollableContainerRef,
              });
            }

            return child;
          })}
          {showControlDots &&
          !_.isNil(scrollableContainerRef.current?.children) ? (
            <div
              className={classes.controlDotWrapper}
              style={{
                ...(showDesignV2 ? { width: getDotContainerWidth() } : {}),
                ...carouselWrapperStyle,
              }}
              data-test-id={`${testId}-dots-container`}
            >
              <div
                className={classes.dotContainer}
                ref={dotCarouselRef}
                style={{
                  ...(showDesignV2
                    ? {
                        transform: `translateX(${getTranslateX()}px)`,
                        transition: "transform 0.3s ease",
                      }
                    : {}),
                }}
              >
                {_.map(
                  scrollableContainerRef.current?.children,
                  (child, index) => {
                    const { isSmallDot, isDotActive } = getCarouselDots(index);

                    return (
                      <button
                        key={index}
                        onClick={() =>
                          handleControlDotClick(scrollAmount * index)
                        }
                        className={classNames({
                          [classes.controlDot]: !showDesignV2,
                          [classes.activeControlDot]:
                            activeControlDot === index + 1 && !showDesignV2,
                          [classes.controlDotV2]: showDesignV2,
                          [classes.activeControlDotV2]:
                            isDotActive && showDesignV2,
                          [classes.smallDot]: isSmallDot && showDesignV2,
                        })}
                        data-test-id={`${testId}-dot-${index}`}
                        aria-label={t("a11y:carousel_go_to_slide", {
                          index: index + 1,
                        })}
                      />
                    );
                  }
                )}
              </div>
            </div>
          ) : null}
        </>
      </ConditionalWrapper>
      {showRightButton ? (
        <button
          onClick={handleRightClick}
          className={scrollRightButton}
          style={rightButtonStyle}
          data-test-id={`${testId}-right-button`}
          aria-label={t("a11y:carousel_next_slide")}
        >
          <ChevronRightOutlined variant={"subtle"} size={chevronIconSize} />
        </button>
      ) : null}
    </div>
  );
};
ScrollableCarousel.displayName = "ScrollableCarousel";

ScrollableCarousel.defaultProps = {
  scrollAmount: 40,
  leftButtonStyle: {},
  rightButtonStyle: {},
  chevronIconSize: "xx-small",
  showControlDots: false,
};

ScrollableCarousel.propTypes = {
  scrollAmount: PropTypes.number,
  leftButtonStyle: PropTypes.object,
  rightButtonStyle: PropTypes.object,
  getScrollableContainerRef: PropTypes.func,
  chevronIconSize: PropTypes.string,
  showControlDots: PropTypes.bool,
};

export default ScrollableCarousel;
